import { useMemo } from 'react'
import cx from 'classnames'
import useI18n from '@/shared/hooks/usei18n'
import { useIsXlUp } from '@/shared/hooks/useIsXlUp'
import { Button, Tooltip } from '@/common'
import { Checkbox } from '@/common/components/Checkbox/Checkbox'
import { ImportedGoodsVirtualRows } from './ImportedGoodsVirtualRows'
import type { ImportedGoodExtended } from '@/features/cbam-reporting/hooks/useImportedGoodsFilters'
import { Partnership } from '@/features/suppliers/hooks/partnerships'
import { Installation } from '@/features/suppliers/hooks/installations'
import { Importer } from '@/features/importers/hooks/importers'
import { GoodsFamily } from '@/common'
import {
  BatchLiabilityResponse,
  BatchLiabilityResultItem
} from '@/features/cbam-reporting/hooks/cbamLiability'
import { useNetMassDisplay } from '@/shared/netMassDisplay/NetMassDisplayUnitContext'

const gridTemplateColumns = '0.4fr 0.4fr 1fr 0.6fr 1.5fr 1.5fr 1fr 0.8fr 1fr 0.8fr 0.4fr'
const gridTemplateColumnsWithScrollbar =
  '0.4fr 0.4fr 1fr 0.6fr 1.5fr 1.5fr 1fr 0.8fr 1fr 0.8fr 0.5fr'

interface ImportedGoodsListProps {
  filteredGoods: ImportedGoodExtended[]
  partnerships: Partnership[]
  importers: Importer[]
  installations: Installation[]
  liabilityData: BatchLiabilityResponse | null
  isRepresentative: boolean
  isCompleted: boolean
  isAllSelected: boolean
  isPartiallySelected: boolean
  hasSelection: boolean
  selectedCount: number
  isSelected: (id: string) => boolean
  toggleSelect: (id: string) => void
  toggleSelectAll: () => void
  deselectAll: () => void
  onEdit: (
    importedGood: ImportedGoodExtended,
    supplierName: string,
    installationName: string,
    goodsFamily?: GoodsFamily
  ) => void
  onView: (importedGood: ImportedGoodExtended) => void
  onDelete: (importedGood: ImportedGoodExtended) => void
  onBulkUpdateSupplierEmissions: () => void
  onBulkUpdateCustoms: () => void
  onBulkUpdateImporter: () => void
  onBulkDelete: () => void
  isSupplierEmissionsBatchDisabled?: boolean
  supplierEmissionsBatchTooltip?: string | null
}

export function ListHeader({
  children,
  gridTemplateColumns,
  className
}: {
  children: React.ReactNode
  gridTemplateColumns: string
  className?: string
}) {
  return (
    <div
      className={cx(
        'grid text-sm text-text-secondary font-medium border-b border-border-default bg-blue-200 items-end gap-2',
        className
      )}
      style={{
        gridTemplateColumns
      }}
    >
      {children}
    </div>
  )
}

export function ListHeaderCell({
  children,
  className
}: {
  children?: React.ReactNode
  className?: string
}) {
  return <div className={cx('p-2', className)}>{children}</div>
}

function BulkUpdateListSelectionRow({
  selectedCount,
  onBulkUpdateSupplierEmissions,
  onBulkUpdateCustoms,
  onBulkUpdateImporter,
  onBulkDelete,
  deselectAll,
  isSupplierEmissionsBatchDisabled,
  supplierEmissionsBatchTooltip
}: {
  selectedCount: number
  onBulkUpdateSupplierEmissions: () => void
  onBulkUpdateCustoms: () => void
  onBulkUpdateImporter: () => void
  onBulkDelete: () => void
  deselectAll: () => void
  isSupplierEmissionsBatchDisabled?: boolean
  supplierEmissionsBatchTooltip?: string | null
}) {
  const { t } = useI18n()
  return (
    <div className="flex items-center justify-between px-4 py-2 bg-white border-b border-border-default">
      <span className="text-sm font-medium">
        {t('cbamReporting:bulkUpdate.selectedCount', { count: selectedCount })}
      </span>
      <div className="flex items-center gap-2 flex-wrap">
        <TooltipOrWrap
          tooltip={supplierEmissionsBatchTooltip}
          disabled={isSupplierEmissionsBatchDisabled}
        >
          <Button
            sizeType="xs"
            variant="subtle"
            onClick={onBulkUpdateSupplierEmissions}
            disabled={isSupplierEmissionsBatchDisabled}
          >
            {t('cbamReporting:bulkUpdate.actions.updateSupplierEmissions')}
          </Button>
        </TooltipOrWrap>
        <Button sizeType="xs" variant="subtle" onClick={onBulkUpdateCustoms}>
          {t('cbamReporting:bulkUpdate.actions.updateCustomsProcedures')}
        </Button>
        <Button sizeType="xs" variant="subtle" onClick={onBulkUpdateImporter}>
          {t('cbamReporting:bulkUpdate.actions.updateImporter')}
        </Button>
        <Button sizeType="xs" variant="danger" onClick={onBulkDelete}>
          {t('cbamReporting:bulkDelete.actions.deleteSelected')}
        </Button>
        <Button sizeType="xs" variant="neutral" onClick={deselectAll}>
          {t('cbamReporting:bulkUpdate.actions.clearSelection')}
        </Button>
      </div>
    </div>
  )
}

function TooltipOrWrap({
  children,
  tooltip,
  disabled
}: {
  children: React.ReactNode
  tooltip?: string | null
  disabled?: boolean
}) {
  const { t } = useI18n()
  if (disabled && tooltip) {
    const content =
      typeof tooltip === 'string' && tooltip.startsWith('cbamReporting:') ? t(tooltip) : tooltip
    return (
      <Tooltip content={content} position="top">
        <span className="inline-block">{children}</span>
      </Tooltip>
    )
  }
  return <>{children}</>
}

function BulkUpdateListSelectionRowCard({
  selectedCount,
  isAllSelected,
  isPartiallySelected,
  onBulkUpdateSupplierEmissions,
  onBulkUpdateCustoms,
  onBulkUpdateImporter,
  onBulkDelete,
  deselectAll,
  toggleSelectAll,
  isSupplierEmissionsBatchDisabled,
  supplierEmissionsBatchTooltip
}: {
  selectedCount: number
  isAllSelected: boolean
  isPartiallySelected: boolean
  onBulkUpdateSupplierEmissions: () => void
  onBulkUpdateCustoms: () => void
  onBulkUpdateImporter: () => void
  onBulkDelete: () => void
  deselectAll: () => void
  toggleSelectAll: () => void
  isSupplierEmissionsBatchDisabled?: boolean
  supplierEmissionsBatchTooltip?: string | null
}) {
  const { t } = useI18n()
  const hasSelection = selectedCount > 0

  return (
    <div className="flex items-center justify-between px-4 py-2 bg-white border-b border-border-default flex-wrap gap-2">
      <div className="flex items-center gap-2">
        <Checkbox
          id="select-all-goods-card"
          checked={isAllSelected}
          onChange={toggleSelectAll}
          className={cx('inline-block', isPartiallySelected ? 'opacity-50' : '')}
        />
        <span className="text-sm font-medium">
          {t('cbamReporting:bulkUpdate.selectedCount', { count: selectedCount })}
        </span>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <TooltipOrWrap
          tooltip={supplierEmissionsBatchTooltip}
          disabled={isSupplierEmissionsBatchDisabled}
        >
          <Button
            sizeType="xs"
            variant="subtle"
            onClick={onBulkUpdateSupplierEmissions}
            disabled={!hasSelection || isSupplierEmissionsBatchDisabled}
          >
            {t('cbamReporting:bulkUpdate.actions.updateSupplierEmissions')}
          </Button>
        </TooltipOrWrap>
        <Button
          sizeType="xs"
          variant="subtle"
          onClick={onBulkUpdateCustoms}
          disabled={!hasSelection}
        >
          {t('cbamReporting:bulkUpdate.actions.updateCustomsProcedures')}
        </Button>
        <Button
          sizeType="xs"
          variant="subtle"
          onClick={onBulkUpdateImporter}
          disabled={!hasSelection}
        >
          {t('cbamReporting:bulkUpdate.actions.updateImporter')}
        </Button>
        <Button sizeType="xs" variant="danger" onClick={onBulkDelete} disabled={!hasSelection}>
          {t('cbamReporting:bulkDelete.actions.deleteSelected')}
        </Button>
        <Button sizeType="xs" variant="neutral" onClick={deselectAll} disabled={!hasSelection}>
          {t('cbamReporting:bulkUpdate.actions.clearSelection')}
        </Button>
      </div>
    </div>
  )
}

export function ListRow({
  children,
  gridTemplateColumns,
  className
}: {
  children: React.ReactNode
  gridTemplateColumns: string
  className?: string
}) {
  return (
    <div
      className={cx(
        'grid text-sm text-text-secondary font-medium border-b border-border-default items-center py-3 gap-2 hover:bg-blue-200 has-[.actions-popover-menu-trigger-open]:bg-blue-200',
        className
      )}
      style={{
        gridTemplateColumns
      }}
    >
      {children}
    </div>
  )
}

export default function ImportedGoodsList({
  filteredGoods,
  partnerships,
  importers,
  installations,
  liabilityData,
  isRepresentative,
  isCompleted,
  isAllSelected,
  isPartiallySelected,
  hasSelection,
  selectedCount,
  isSelected,
  toggleSelect,
  toggleSelectAll,
  deselectAll,
  onEdit,
  onView,
  onDelete,
  onBulkUpdateSupplierEmissions,
  onBulkUpdateCustoms,
  onBulkUpdateImporter,
  onBulkDelete,
  isSupplierEmissionsBatchDisabled,
  supplierEmissionsBatchTooltip
}: ImportedGoodsListProps) {
  const { t } = useI18n()
  const { netMassColumnLabel } = useNetMassDisplay()
  const isXlUp = useIsXlUp()

  const partnershipById = useMemo(
    () => new Map(partnerships.map(partnership => [partnership.id, partnership])),
    [partnerships]
  )
  const importerById = useMemo(
    () => new Map(importers.map(importer => [importer.id, importer])),
    [importers]
  )
  const installationById = useMemo(
    () => new Map(installations.map(installation => [installation.id, installation])),
    [installations]
  )

  // Create a map from liability results keyed by id for efficient lookup
  // Falls back to index-based mapping for backward compatibility
  const liabilityDataMap = useMemo(() => {
    if (!liabilityData?.results) {
      return null
    }

    const map = new Map<string, BatchLiabilityResultItem>()
    const hasIds = liabilityData.results.some(result => result.id !== undefined)

    if (hasIds) {
      // Use ID-based mapping when IDs are available
      liabilityData.results.forEach(result => {
        if (result.id) {
          map.set(result.id, result)
        }
      })
    }

    return { map, hasIds }
  }, [liabilityData])

  // Helper function to get liability data for a good
  const getLiabilityDataForGood = (
    good: ImportedGoodExtended,
    index: number
  ): BatchLiabilityResultItem | null => {
    if (!liabilityData?.results) {
      return null
    }

    // Use ID-based lookup if available, otherwise fall back to index
    if (liabilityDataMap?.hasIds && good.id) {
      return liabilityDataMap.map.get(good.id) ?? null
    }

    // Fallback to index-based mapping for backward compatibility
    return liabilityData.results[index] ?? null
  }

  const gridColsForHeader =
    filteredGoods.length > 8 ? gridTemplateColumnsWithScrollbar : gridTemplateColumns

  return (
    <div className="border border-border-default rounded-xs overflow-x-auto w-full">
      {isXlUp && (
        <ListHeader className="grid" gridTemplateColumns={gridColsForHeader}>
          {!isCompleted && (
            <div className="flex items-center justify-center p-2">
              <Checkbox
                id="select-all-goods"
                checked={isAllSelected}
                onChange={toggleSelectAll}
                className={cx('inline-block', isPartiallySelected ? 'opacity-50' : '')}
              />
            </div>
          )}
          <ListHeaderCell className="text-center">
            {t('cbamReporting:cbamReportDetails.reportedGoods.table.headers.status')}
          </ListHeaderCell>
          <ListHeaderCell>
            {t('cbamReporting:cbamReportDetails.reportedGoods.table.headers.goods')}
          </ListHeaderCell>
          <ListHeaderCell>
            {t('cbamReporting:cbamReportDetails.reportedGoods.table.headers.countryOfOrigin')}
          </ListHeaderCell>
          <ListHeaderCell>
            {t('cbamReporting:cbamReportDetails.reportedGoods.table.headers.producer')}
          </ListHeaderCell>
          {isRepresentative && (
            <ListHeaderCell>
              {t('cbamReporting:cbamReportDetails.reportedGoods.table.headers.importer')}
            </ListHeaderCell>
          )}
          <ListHeaderCell>
            {t('cbamReporting:cbamReportDetails.reportedGoods.table.headers.seller')}
          </ListHeaderCell>
          <ListHeaderCell className="text-right">{netMassColumnLabel}</ListHeaderCell>
          <ListHeaderCell>
            {t('cbamReporting:cbamReportDetails.reportedGoods.table.headers.emissionsType')}
          </ListHeaderCell>
          <ListHeaderCell className="text-right">
            {t(
              'cbamReporting:cbamReportDetails.reportedGoods.table.headers.totalCertificatesRequired'
            )}
          </ListHeaderCell>
          <ListHeaderCell />
        </ListHeader>
      )}
      {hasSelection && isXlUp && (
        <BulkUpdateListSelectionRow
          selectedCount={selectedCount}
          onBulkUpdateSupplierEmissions={onBulkUpdateSupplierEmissions}
          onBulkUpdateCustoms={onBulkUpdateCustoms}
          onBulkUpdateImporter={onBulkUpdateImporter}
          onBulkDelete={onBulkDelete}
          deselectAll={deselectAll}
          isSupplierEmissionsBatchDisabled={isSupplierEmissionsBatchDisabled}
          supplierEmissionsBatchTooltip={supplierEmissionsBatchTooltip}
        />
      )}
      {!isCompleted && !isXlUp && (
        <BulkUpdateListSelectionRowCard
          selectedCount={selectedCount}
          isAllSelected={isAllSelected}
          isPartiallySelected={isPartiallySelected}
          onBulkUpdateSupplierEmissions={onBulkUpdateSupplierEmissions}
          onBulkUpdateCustoms={onBulkUpdateCustoms}
          onBulkUpdateImporter={onBulkUpdateImporter}
          onBulkDelete={onBulkDelete}
          deselectAll={deselectAll}
          toggleSelectAll={toggleSelectAll}
          isSupplierEmissionsBatchDisabled={isSupplierEmissionsBatchDisabled}
          supplierEmissionsBatchTooltip={supplierEmissionsBatchTooltip}
        />
      )}
      <ImportedGoodsVirtualRows
        key={isXlUp ? 'table' : 'card'}
        layout={isXlUp ? 'table' : 'card'}
        filteredGoods={filteredGoods}
        gridTemplateColumns={gridColsForHeader}
        partnershipById={partnershipById}
        importerById={importerById}
        installationById={installationById}
        getLiabilityDataForGood={getLiabilityDataForGood}
        isRepresentative={isRepresentative}
        isCompleted={isCompleted}
        isSelected={isSelected}
        toggleSelect={toggleSelect}
        onEdit={onEdit}
        onView={onView}
        onDelete={onDelete}
      />
    </div>
  )
}
